import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Plus, MoreVertical, Edit, Trash2, ExternalLink, 
  Upload, Image, Star, Globe, Settings, Copy, Info,
  ChevronUp, ChevronDown, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-slate-100 text-slate-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-amber-100 text-amber-800'
};

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter (Modern)' },
  { value: 'Poppins', label: 'Poppins (Friendly)' },
  { value: 'Roboto', label: 'Roboto (Clean)' },
  { value: 'Open Sans', label: 'Open Sans (Readable)' },
  { value: 'Lato', label: 'Lato (Professional)' },
  { value: 'Montserrat', label: 'Montserrat (Bold)' }
];

// Image dimension guidelines
const IMAGE_SPECS = {
  hero_desktop: { width: 1920, height: 900, ratio: '2.13:1', maxSize: '2.5MB' },
  hero_mobile: { width: 1080, height: 1350, ratio: '4:5', maxSize: '2.5MB' },
  trust: { width: 600, height: 300, ratio: '2:1', maxSize: '800KB' },
  offer: { width: 900, height: 600, ratio: '3:2', maxSize: '1.2MB' },
  logo: { width: 240, height: 80, ratio: '3:1', maxSize: '500KB' }
};

// Upload hint component
const UploadHint = ({ type }) => {
  const spec = IMAGE_SPECS[type];
  if (!spec) return null;
  return (
    <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
      <Info size={12} />
      {spec.width}×{spec.height}px ({spec.ratio}) • Max {spec.maxSize}
    </div>
  );
};

export default function LandingPagesPage() {
  const { role } = usePermissions();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isGlobalSettingsOpen, setIsGlobalSettingsOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    status: 'draft',
    is_default: false,
    hero_image_url: '',
    hero_mobile_image_url: '',
    youtube_url: '',
    youtube_url_2: '',
    trust_images: [],
    offer_tiles: [],
    footer_text: '',
    disclaimer_text: '',
    button_color: '#2563EB',
    button_text_color: '#FFFFFF',
    button_text_primary: 'Check Eligibility',
    button_text_secondary: 'Contact Us',
    btn_primary_font_size: '16',
    btn_primary_size: 'md',
    btn_primary_font_family: '',
    btn_secondary_bg_color: '',
    btn_secondary_text_color: '',
    btn_secondary_font_size: '16',
    btn_secondary_size: 'md',
    btn_secondary_font_family: '',
    button_layout: 'side_by_side',
    font_family: 'Inter',
    background_color: '#FFFFFF',
    background_gradient_end: '',
    background_gradient_direction: 'to bottom',
    background_opacity: 1.0,
    form_fields: [],
    thank_you_message: 'Thank you for your interest! Our team will contact you shortly.'
  });
  const [globalSettings, setGlobalSettings] = useState({
    logo_url: '',
    phone_number: '',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    linkedin_url: '',
    whatsapp_url: ''
  });
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const canManage = ['admin', 'owner'].includes(role);

  const fetchPages = useCallback(async () => {
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/landing-pages`
        : `${API_URL}/api/landing-pages?status=${statusFilter}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      logger.error('Error fetching landing pages:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchGlobalSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/landing-pages/global-settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setGlobalSettings({
          logo_url: data.logo_url || '',
          phone_number: data.phone_number || '',
          facebook_url: data.facebook_url || '',
          instagram_url: data.instagram_url || '',
          youtube_url: data.youtube_url || '',
          linkedin_url: data.linkedin_url || '',
          whatsapp_url: data.whatsapp_url || ''
        });
      }
    } catch (error) {
      logger.error('Error fetching global settings:', error);
    }
  };

  useEffect(() => {
    fetchPages();
    fetchGlobalSettings();
  }, [fetchPages]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadImage = async (file, imageType, field) => {
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/landing-pages/upload-image?image_type=${imageType}`, {
        method: 'POST',
        body: uploadData,
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (field === 'hero') {
          setFormData(prev => ({ ...prev, hero_image_url: result.url }));
        } else if (field === 'hero_mobile') {
          setFormData(prev => ({ ...prev, hero_mobile_image_url: result.url }));
        } else if (field === 'trust') {
          setFormData(prev => ({ 
            ...prev, 
            trust_images: [...prev.trust_images, result.url].slice(0, 3) 
          }));
        } else if (field.startsWith('offer_')) {
          const idx = parseInt(field.split('_')[1]);
          setFormData(prev => {
            const tiles = [...prev.offer_tiles];
            if (!tiles[idx]) tiles[idx] = { image_url: '', caption: '' };
            tiles[idx].image_url = result.url;
            return { ...prev, offer_tiles: tiles };
          });
        } else if (field === 'logo') {
          setGlobalSettings(prev => ({ ...prev, logo_url: result.url }));
        }
        
        toast.success(`Image uploaded (${result.width}×${result.height}px)`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.slug.trim()) {
      toast.error('Slug is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        slug: formData.slug.toLowerCase().replace(/\s+/g, '-'),
        offer_tiles: formData.offer_tiles.filter(t => t.image_url),
        // Strip empty option strings from any dropdown fields so the save
        // request is clean and the rendered <Select> doesn't show blanks.
        form_fields: (formData.form_fields || []).map(f =>
          f.field_type === 'dropdown'
            ? { ...f, options: (f.options || []).map(o => (o || '').trim()).filter(Boolean) }
            : f
        ),
      };

      const url = editingPage 
        ? `${API_URL}/api/landing-pages/${editingPage.landing_page_id}`
        : `${API_URL}/api/landing-pages`;
      
      const response = await fetch(url, {
        method: editingPage ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(editingPage ? 'Landing page updated' : 'Landing page created');
        setIsEditorOpen(false);
        resetForm();
        fetchPages();
      } else {
        const error = await response.json().catch(() => ({}));
        // Surface backend validation detail more usefully. Pydantic v2 returns
        // detail as a list of {loc, msg, ...} for 422; FastAPI HTTPException
        // returns detail as a string for explicit raises.
        let msg = 'Failed to save';
        if (typeof error.detail === 'string') {
          msg = error.detail;
        } else if (Array.isArray(error.detail) && error.detail.length) {
          const first = error.detail[0];
          msg = `${(first.loc || []).slice(-1)[0] || 'field'}: ${first.msg || 'invalid'}`;
        }
        toast.error(msg);
      }
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save');
    }
  };

  const handleSaveGlobalSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/landing-pages/global-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(globalSettings),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Global settings saved');
        setIsGlobalSettingsOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save global settings');
    }
  };

  const handleDelete = async (pageId) => {
    if (!confirm('Are you sure you want to delete this landing page?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/landing-pages/${pageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Landing page deleted');
        fetchPages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const openEditor = (page = null) => {
    if (page) {
      setEditingPage(page);
      setFormData({
        title: page.title || '',
        slug: page.slug || '',
        status: page.status || 'draft',
        is_default: page.is_default || false,
        hero_image_url: page.hero_image_url || '',
        hero_mobile_image_url: page.hero_mobile_image_url || '',
        youtube_url: page.youtube_url || '',
        youtube_url_2: page.youtube_url_2 || '',
        trust_images: page.trust_images || [],
        offer_tiles: page.offer_tiles || [],
        footer_text: page.footer_text || '',
        disclaimer_text: page.disclaimer_text || '',
        button_color: page.button_color || '#2563EB',
        button_text_color: page.button_text_color || '#FFFFFF',
        button_text_primary: page.button_text_primary || 'Check Eligibility',
        button_text_secondary: page.button_text_secondary || 'Contact Us',
        btn_primary_font_size: page.btn_primary_font_size || '16',
        btn_primary_size: page.btn_primary_size || 'md',
        btn_primary_font_family: page.btn_primary_font_family || '',
        btn_secondary_bg_color: page.btn_secondary_bg_color || '',
        btn_secondary_text_color: page.btn_secondary_text_color || '',
        btn_secondary_font_size: page.btn_secondary_font_size || '16',
        btn_secondary_size: page.btn_secondary_size || 'md',
        btn_secondary_font_family: page.btn_secondary_font_family || '',
        button_layout: page.button_layout || 'side_by_side',
        font_family: page.font_family || 'Inter',
        background_color: page.background_color || '#FFFFFF',
        background_gradient_end: page.background_gradient_end || '',
        background_gradient_direction: page.background_gradient_direction || 'to bottom',
        background_opacity: page.background_opacity ?? 1.0,
        form_fields: page.form_fields || [],
        thank_you_message: page.thank_you_message || 'Thank you for your interest! Our team will contact you shortly.'
      });
    } else {
      resetForm();
    }
    setIsEditorOpen(true);
  };

  const resetForm = () => {
    setEditingPage(null);
    setFormData({
      title: '',
      slug: '',
      status: 'draft',
      is_default: false,
      hero_image_url: '',
      hero_mobile_image_url: '',
      youtube_url: '',
      youtube_url_2: '',
      trust_images: [],
      offer_tiles: [],
      footer_text: '',
      disclaimer_text: '',
      button_color: '#2563EB',
      button_text_color: '#FFFFFF',
      button_text_primary: 'Check Eligibility',
      button_text_secondary: 'Contact Us',
      btn_primary_font_size: '16',
      btn_primary_size: 'md',
      btn_primary_font_family: '',
      btn_secondary_bg_color: '',
      btn_secondary_text_color: '',
      btn_secondary_font_size: '16',
      btn_secondary_size: 'md',
      btn_secondary_font_family: '',
      button_layout: 'side_by_side',
      font_family: 'Inter',
      background_color: '#FFFFFF',
      background_gradient_end: '',
      background_gradient_direction: 'to bottom',
      background_opacity: 1.0,
      form_fields: [],
      thank_you_message: 'Thank you for your interest! Our team will contact you shortly.'
    });
  };

  const copyPageUrl = (slug) => {
    const url = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const publishedCount = pages.filter(p => p.status === 'published').length;

  if (!canManage) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500 mt-2">You don't have permission to manage landing pages.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="landing-pages-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Landing Pages</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage public intake funnels • {publishedCount}/5 published
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsGlobalSettingsOpen(true)}>
            <Settings size={16} className="mr-1" /> Global Settings
          </Button>
          <Button onClick={() => openEditor()} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={16} className="mr-1" /> New Landing Page
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Landing Pages Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : pages.length === 0 ? (
            <div className="p-12 text-center">
              <Globe className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No Landing Pages</h3>
              <p className="text-slate-500 mt-1">Create your first landing page to start capturing leads.</p>
              <Button onClick={() => openEditor()} className="mt-4">
                <Plus size={16} className="mr-1" /> Create Landing Page
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Title</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pages.map(page => (
                  <TableRow key={page.landing_page_id} className="hover:bg-slate-50/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {page.hero_image_url ? (
                          <img 
                            src={`${API_URL}${page.hero_image_url}`} 
                            alt="" 
                            className="w-12 h-8 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-8 bg-slate-100 rounded flex items-center justify-center">
                            <Image size={14} className="text-slate-400" />
                          </div>
                        )}
                        <span className="font-medium">{page.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">/lp/{page.slug}</code>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[page.status]}>{page.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {page.is_default && <Star size={16} className="text-amber-500 fill-amber-500" />}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {format(new Date(page.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditor(page)}>
                            <Edit size={14} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          {page.status === 'published' && (
                            <DropdownMenuItem onClick={() => window.open(`/lp/${page.slug}`, '_blank')}>
                              <ExternalLink size={14} className="mr-2" /> Preview
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => copyPageUrl(page.slug)}>
                            <Copy size={14} className="mr-2" /> Copy URL
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(page.landing_page_id)}
                            className="text-red-600"
                          >
                            <Trash2 size={14} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPage ? 'Edit Landing Page' : 'Create Landing Page'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="content" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="style">Buttons & Style</TabsTrigger>
              <TabsTrigger value="form">Lead Form</TabsTrigger>
            </TabsList>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Investment Opportunity"
                  />
                </div>
                <div>
                  <Label>Slug *</Label>
                  <div className="flex items-center">
                    <span className="text-sm text-slate-500 mr-1">/lp/</span>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') 
                      }))}
                      placeholder="investment-2026"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                    className="rounded"
                  />
                  <Label className="text-sm">Set as default landing page</Label>
                </div>
              </div>

              {/* YouTube Videos */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">YouTube Videos</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Video 1 (optional)</Label>
                    <Input
                      value={formData.youtube_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Video 2 (optional)</Label>
                    <Input
                      value={formData.youtube_url_2}
                      onChange={(e) => setFormData(prev => ({ ...prev, youtube_url_2: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Footer Text</Label>
                  <Textarea
                    value={formData.footer_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, footer_text: e.target.value }))}
                    placeholder="© 2026 Company Name"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Disclaimer Text</Label>
                  <Textarea
                    value={formData.disclaimer_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, disclaimer_text: e.target.value }))}
                    placeholder="Terms and conditions apply..."
                    rows={2}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Images Tab */}
            <TabsContent value="images" className="space-y-6 py-4">
              {/* Hero Images */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Hero Banner *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Desktop (required)</p>
                    {formData.hero_image_url ? (
                      <div className="relative">
                        <img 
                          src={`${API_URL}${formData.hero_image_url}`} 
                          alt="Hero" 
                          className="w-full h-32 object-cover rounded"
                        />
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          className="absolute top-2 right-2"
                          onClick={() => setFormData(prev => ({ ...prev, hero_image_url: '' }))}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                        <Upload size={24} className="text-slate-400" />
                        <span className="text-sm text-slate-500 mt-1">Upload Desktop Hero</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={(e) => handleUploadImage(e.target.files?.[0], 'hero_desktop', 'hero')}
                          disabled={uploading}
                        />
                      </label>
                    )}
                    <UploadHint type="hero_desktop" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Mobile (recommended)</p>
                    {formData.hero_mobile_image_url ? (
                      <div className="relative">
                        <img 
                          src={`${API_URL}${formData.hero_mobile_image_url}`} 
                          alt="Hero Mobile" 
                          className="w-full h-32 object-cover rounded"
                        />
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          className="absolute top-2 right-2"
                          onClick={() => setFormData(prev => ({ ...prev, hero_mobile_image_url: '' }))}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                        <Upload size={24} className="text-slate-400" />
                        <span className="text-sm text-slate-500 mt-1">Upload Mobile Hero</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={(e) => handleUploadImage(e.target.files?.[0], 'hero_mobile', 'hero_mobile')}
                          disabled={uploading}
                        />
                      </label>
                    )}
                    <UploadHint type="hero_mobile" />
                  </div>
                </div>
              </div>

              {/* Trust Images */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Trust Strip (max 3 images)</Label>
                <UploadHint type="trust" />
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[0, 1, 2].map(idx => (
                    <div key={`trust-${idx}`}>
                      {formData.trust_images[idx] ? (
                        <div className="relative">
                          <img 
                            src={`${API_URL}${formData.trust_images[idx]}`} 
                            alt={`Trust ${idx + 1}`} 
                            className="w-full h-20 object-contain rounded bg-slate-100"
                          />
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              trust_images: prev.trust_images.filter((_, i) => i !== idx)
                            }))}
                          >
                            <Trash2 size={10} />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed rounded cursor-pointer hover:bg-slate-50">
                          <Upload size={16} className="text-slate-400" />
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={(e) => handleUploadImage(e.target.files?.[0], 'trust', 'trust')}
                            disabled={uploading || formData.trust_images.length >= 3}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Offer Tiles */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Offer Tiles (max 3)</Label>
                <UploadHint type="offer" />
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[0, 1, 2].map(idx => (
                    <div key={`offer-${idx}`} className="space-y-2">
                      {formData.offer_tiles[idx]?.image_url ? (
                        <div className="relative">
                          <img 
                            src={`${API_URL}${formData.offer_tiles[idx].image_url}`} 
                            alt={`Offer ${idx + 1}`} 
                            className="w-full h-24 object-cover rounded"
                          />
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            onClick={() => setFormData(prev => {
                              const tiles = [...prev.offer_tiles];
                              tiles[idx] = { image_url: '', caption: '' };
                              return { ...prev, offer_tiles: tiles };
                            })}
                          >
                            <Trash2 size={10} />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed rounded cursor-pointer hover:bg-slate-50">
                          <Upload size={16} className="text-slate-400" />
                          <span className="text-xs text-slate-500">Offer {idx + 1}</span>
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={(e) => handleUploadImage(e.target.files?.[0], 'offer', `offer_${idx}`)}
                            disabled={uploading}
                          />
                        </label>
                      )}
                      <Input
                        placeholder="Caption (optional)"
                        value={formData.offer_tiles[idx]?.caption || ''}
                        onChange={(e) => setFormData(prev => {
                          const tiles = [...prev.offer_tiles];
                          if (!tiles[idx]) tiles[idx] = { image_url: '', caption: '' };
                          tiles[idx].caption = e.target.value;
                          return { ...prev, offer_tiles: tiles };
                        })}
                        className="text-xs"
                      />
                      {/* Click Action */}
                      {formData.offer_tiles[idx]?.image_url && (
                        <div className="space-y-1">
                          <Select
                            value={formData.offer_tiles[idx]?.click_action || 'form'}
                            onValueChange={(v) => setFormData(prev => {
                              const tiles = [...prev.offer_tiles];
                              tiles[idx] = { ...tiles[idx], click_action: v };
                              return { ...prev, offer_tiles: tiles };
                            })}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="On click..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="form">Open Lead Form</SelectItem>
                              <SelectItem value="url">Go to URL</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.offer_tiles[idx]?.click_action === 'url' && (
                            <Input
                              placeholder="https://..."
                              value={formData.offer_tiles[idx]?.click_url || ''}
                              onChange={(e) => setFormData(prev => {
                                const tiles = [...prev.offer_tiles];
                                tiles[idx] = { ...tiles[idx], click_url: e.target.value };
                                return { ...prev, offer_tiles: tiles };
                              })}
                              className="text-xs h-7"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Style Tab */}
            <TabsContent value="style" className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
              {/* Primary Button */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Primary Button</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Button Text</Label>
                    <Input value={formData.button_text_primary} onChange={(e) => setFormData(prev => ({ ...prev, button_text_primary: e.target.value }))} placeholder="Check Eligibility" className="mt-1" data-testid="btn-text-primary" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Font Family</Label>
                    <Select value={formData.btn_primary_font_family || formData.font_family} onValueChange={(v) => setFormData(prev => ({ ...prev, btn_primary_font_family: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Background Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={formData.button_color} onChange={(e) => setFormData(prev => ({ ...prev, button_color: e.target.value }))} className="w-12 h-9 p-1 cursor-pointer" />
                      <Input value={formData.button_color} onChange={(e) => setFormData(prev => ({ ...prev, button_color: e.target.value }))} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Text Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={formData.button_text_color} onChange={(e) => setFormData(prev => ({ ...prev, button_text_color: e.target.value }))} className="w-12 h-9 p-1 cursor-pointer" />
                      <Input value={formData.button_text_color} onChange={(e) => setFormData(prev => ({ ...prev, button_text_color: e.target.value }))} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Font Size (px)</Label>
                    <Select value={formData.btn_primary_font_size} onValueChange={(v) => setFormData(prev => ({ ...prev, btn_primary_font_size: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{['12','14','16','18','20','24'].map(s => <SelectItem key={s} value={s}>{s}px</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Button Size</Label>
                    <Select value={formData.btn_primary_size} onValueChange={(v) => setFormData(prev => ({ ...prev, btn_primary_size: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="md">Medium</SelectItem>
                        <SelectItem value="lg">Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Secondary Button */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Secondary Button</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Button Text</Label>
                    <Input value={formData.button_text_secondary} onChange={(e) => setFormData(prev => ({ ...prev, button_text_secondary: e.target.value }))} placeholder="Contact Us" className="mt-1" data-testid="btn-text-secondary" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Font Family</Label>
                    <Select value={formData.btn_secondary_font_family || formData.font_family} onValueChange={(v) => setFormData(prev => ({ ...prev, btn_secondary_font_family: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Background Color (empty = outline)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={formData.btn_secondary_bg_color || '#FFFFFF'} onChange={(e) => setFormData(prev => ({ ...prev, btn_secondary_bg_color: e.target.value }))} className="w-12 h-9 p-1 cursor-pointer" />
                      <Input value={formData.btn_secondary_bg_color} onChange={(e) => setFormData(prev => ({ ...prev, btn_secondary_bg_color: e.target.value }))} placeholder="Empty = outline style" className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Text Color (empty = primary color)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={formData.btn_secondary_text_color || formData.button_color} onChange={(e) => setFormData(prev => ({ ...prev, btn_secondary_text_color: e.target.value }))} className="w-12 h-9 p-1 cursor-pointer" />
                      <Input value={formData.btn_secondary_text_color} onChange={(e) => setFormData(prev => ({ ...prev, btn_secondary_text_color: e.target.value }))} placeholder="Empty = primary color" className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Font Size (px)</Label>
                    <Select value={formData.btn_secondary_font_size} onValueChange={(v) => setFormData(prev => ({ ...prev, btn_secondary_font_size: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{['12','14','16','18','20','24'].map(s => <SelectItem key={s} value={s}>{s}px</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Button Size</Label>
                    <Select value={formData.btn_secondary_size} onValueChange={(v) => setFormData(prev => ({ ...prev, btn_secondary_size: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="md">Medium</SelectItem>
                        <SelectItem value="lg">Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Button Layout */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Button Layout</Label>
                <Select value={formData.button_layout} onValueChange={(v) => setFormData(prev => ({ ...prev, button_layout: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="side_by_side">Side by Side</SelectItem>
                    <SelectItem value="stacked">Stacked (Vertical)</SelectItem>
                    <SelectItem value="primary_only">Primary Only</SelectItem>
                    <SelectItem value="secondary_only">Secondary Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Global Font & Background */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Global Font</Label>
                <Select value={formData.font_family} onValueChange={(v) => setFormData(prev => ({ ...prev, font_family: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Background</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Start Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={formData.background_color} onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))} className="w-12 h-9 p-1 cursor-pointer" />
                      <Input value={formData.background_color} onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">End Color (empty = solid)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="color" value={formData.background_gradient_end || '#FFFFFF'} onChange={(e) => setFormData(prev => ({ ...prev, background_gradient_end: e.target.value }))} className="w-12 h-9 p-1 cursor-pointer" />
                      <Input value={formData.background_gradient_end} onChange={(e) => setFormData(prev => ({ ...prev, background_gradient_end: e.target.value }))} placeholder="Empty = solid" className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Direction</Label>
                    <Select value={formData.background_gradient_direction} onValueChange={(v) => setFormData(prev => ({ ...prev, background_gradient_direction: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="to bottom">Top to Bottom</SelectItem>
                        <SelectItem value="to right">Left to Right</SelectItem>
                        <SelectItem value="to bottom right">Diagonal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Opacity ({Math.round(formData.background_opacity * 100)}%)</Label>
                    <input type="range" min="0" max="1" step="0.05" value={formData.background_opacity} onChange={(e) => setFormData(prev => ({ ...prev, background_opacity: parseFloat(e.target.value) }))} className="w-full mt-3" />
                  </div>
                </div>
              </div>

              {/* Button Preview */}
              <div className="border rounded-lg p-4">
                <Label className="text-sm font-semibold mb-3 block">Preview</Label>
                <div className={`flex gap-4 ${formData.button_layout === 'stacked' ? 'flex-col items-start' : 'items-center'}`}>
                  {formData.button_layout !== 'secondary_only' && (
                    <button className="rounded-lg font-medium transition-all" style={{
                      backgroundColor: formData.button_color, color: formData.button_text_color,
                      fontFamily: formData.btn_primary_font_family || formData.font_family,
                      fontSize: `${formData.btn_primary_font_size}px`,
                      padding: formData.btn_primary_size === 'sm' ? '6px 16px' : formData.btn_primary_size === 'lg' ? '16px 40px' : '10px 24px'
                    }}>
                      {formData.button_text_primary || 'Check Eligibility'}
                    </button>
                  )}
                  {formData.button_layout !== 'primary_only' && (
                    <button className="rounded-lg font-medium border-2 transition-all" style={{
                      backgroundColor: formData.btn_secondary_bg_color || 'transparent',
                      borderColor: formData.btn_secondary_bg_color ? 'transparent' : formData.button_color,
                      color: formData.btn_secondary_text_color || formData.button_color,
                      fontFamily: formData.btn_secondary_font_family || formData.font_family,
                      fontSize: `${formData.btn_secondary_font_size}px`,
                      padding: formData.btn_secondary_size === 'sm' ? '6px 16px' : formData.btn_secondary_size === 'lg' ? '16px 40px' : '10px 24px'
                    }}>
                      {formData.button_text_secondary || 'Contact Us'}
                    </button>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Lead Form Tab */}
            <TabsContent value="form" className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
              {/* Thank You Message */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-sm font-semibold">Thank You Message (after submission)</Label>
                <Textarea
                  value={formData.thank_you_message}
                  onChange={(e) => setFormData(prev => ({ ...prev, thank_you_message: e.target.value }))}
                  placeholder="Thank you for your interest!"
                  className="min-h-[60px]"
                />
              </div>

              {/* Form Fields Configuration */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Form Fields</Label>
                  <Button variant="outline" size="sm" onClick={() => {
                    const newField = {
                      field_id: `custom_${Date.now()}`,
                      label: 'New Field',
                      field_type: 'text',
                      required: false,
                      visible: true,
                      order: (formData.form_fields || []).length,
                      options: [],
                      placeholder: ''
                    };
                    setFormData(prev => ({
                      ...prev,
                      form_fields: [...(prev.form_fields || []), newField]
                    }));
                  }}>
                    <Plus size={14} className="mr-1" /> Add Field
                  </Button>
                </div>

                {(formData.form_fields || []).length === 0 && (
                  <div className="text-center py-4 text-slate-500 text-sm">
                    <p>No custom fields configured. Default form will be used.</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        form_fields: [
                          { field_id: 'first_name', label: 'First Name', field_type: 'text', required: true, visible: true, order: 0, options: [], placeholder: 'Enter first name' },
                          { field_id: 'last_name', label: 'Last Name', field_type: 'text', required: true, visible: true, order: 1, options: [], placeholder: 'Enter last name' },
                          { field_id: 'phone', label: 'Phone Number', field_type: 'text', required: true, visible: true, order: 2, options: [], placeholder: 'Enter phone number' },
                          { field_id: 'email', label: 'Email', field_type: 'text', required: false, visible: true, order: 3, options: [], placeholder: 'Enter email' },
                          { field_id: 'state', label: 'State', field_type: 'dropdown', required: false, visible: true, order: 4, options: [], placeholder: 'Select state' },
                          { field_id: 'interested_product', label: 'Interested Product', field_type: 'dropdown', required: false, visible: true, order: 5, options: [], placeholder: 'Select product' },
                          { field_id: 'notes', label: 'Notes / Comments', field_type: 'text', required: false, visible: true, order: 6, options: [], placeholder: 'Any additional notes' },
                          { field_id: 'consent', label: 'I agree to be contacted regarding my inquiry', field_type: 'checkbox', required: true, visible: true, order: 7, options: [] },
                        ]
                      }));
                    }}>
                      Load Default Fields
                    </Button>
                  </div>
                )}

                <div className="space-y-2">
                  {(formData.form_fields || [])
                    .sort((a, b) => a.order - b.order)
                    .map((field, idx) => (
                    <div key={field.field_id} className="border rounded p-3 bg-slate-50/50 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" disabled={idx === 0}
                            onClick={() => {
                              const fields = [...formData.form_fields].sort((a, b) => a.order - b.order);
                              if (idx > 0) { fields[idx].order = idx - 1; fields[idx - 1].order = idx; }
                              setFormData(prev => ({ ...prev, form_fields: fields }));
                            }}>
                            <ChevronUp size={12} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" disabled={idx === formData.form_fields.length - 1}
                            onClick={() => {
                              const fields = [...formData.form_fields].sort((a, b) => a.order - b.order);
                              if (idx < fields.length - 1) { fields[idx].order = idx + 1; fields[idx + 1].order = idx; }
                              setFormData(prev => ({ ...prev, form_fields: fields }));
                            }}>
                            <ChevronDown size={12} />
                          </Button>
                        </div>
                        <Input
                          value={field.label}
                          onChange={(e) => {
                            const fields = [...formData.form_fields];
                            const fi = fields.findIndex(f => f.field_id === field.field_id);
                            fields[fi] = { ...fields[fi], label: e.target.value };
                            setFormData(prev => ({ ...prev, form_fields: fields }));
                          }}
                          className="flex-1 h-8 text-sm font-medium"
                        />
                        <Select value={field.field_type} onValueChange={(v) => {
                          const fields = [...formData.form_fields];
                          const fi = fields.findIndex(f => f.field_id === field.field_id);
                          fields[fi] = { ...fields[fi], field_type: v };
                          setFormData(prev => ({ ...prev, form_fields: fields }));
                        }}>
                          <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="dropdown">Dropdown</SelectItem>
                            <SelectItem value="checkbox">Checkbox</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer">
                          <input type="checkbox" checked={field.required} onChange={(e) => {
                            const fields = [...formData.form_fields];
                            const fi = fields.findIndex(f => f.field_id === field.field_id);
                            fields[fi] = { ...fields[fi], required: e.target.checked };
                            setFormData(prev => ({ ...prev, form_fields: fields }));
                          }} className="rounded" />
                          Required
                        </label>
                        <label className="flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer">
                          <input type="checkbox" checked={field.visible} onChange={(e) => {
                            const fields = [...formData.form_fields];
                            const fi = fields.findIndex(f => f.field_id === field.field_id);
                            fields[fi] = { ...fields[fi], visible: e.target.checked };
                            setFormData(prev => ({ ...prev, form_fields: fields }));
                          }} className="rounded" />
                          Visible
                        </label>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              form_fields: prev.form_fields.filter(f => f.field_id !== field.field_id)
                            }));
                          }}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      {field.field_type === 'dropdown' && (
                        <div className="ml-8 space-y-1">
                          <Label className="text-xs text-slate-500">Dropdown Options</Label>
                          <div className="space-y-1.5">
                            {(field.options || []).map((opt, oi) => (
                              <div key={`${field.field_id}-opt-${oi}`} className="flex gap-1.5">
                                <Input
                                  value={opt}
                                  onChange={(e) => {
                                    const fields = [...formData.form_fields];
                                    const fi = fields.findIndex(f => f.field_id === field.field_id);
                                    const next = [...(fields[fi].options || [])];
                                    next[oi] = e.target.value;
                                    fields[fi] = { ...fields[fi], options: next };
                                    setFormData(prev => ({ ...prev, form_fields: fields }));
                                  }}
                                  placeholder={`Option ${oi + 1}`}
                                  className="text-xs h-7 flex-1"
                                  data-testid={`field-${field.field_id}-option-${oi}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                                  onClick={() => {
                                    const fields = [...formData.form_fields];
                                    const fi = fields.findIndex(f => f.field_id === field.field_id);
                                    const next = (fields[fi].options || []).filter((_, i) => i !== oi);
                                    fields[fi] = { ...fields[fi], options: next };
                                    setFormData(prev => ({ ...prev, form_fields: fields }));
                                  }}
                                  data-testid={`field-${field.field_id}-remove-option-${oi}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={() => {
                                const fields = [...formData.form_fields];
                                const fi = fields.findIndex(f => f.field_id === field.field_id);
                                fields[fi] = { ...fields[fi], options: [...(fields[fi].options || []), ''] };
                                setFormData(prev => ({ ...prev, form_fields: fields }));
                              }}
                              data-testid={`field-${field.field_id}-add-option`}
                            >
                              + Add Option
                            </Button>
                          </div>
                        </div>
                      )}
                      {field.field_type !== 'checkbox' && (
                        <div className="ml-8">
                          <Input
                            value={field.placeholder || ''}
                            onChange={(e) => {
                              const fields = [...formData.form_fields];
                              const fi = fields.findIndex(f => f.field_id === field.field_id);
                              fields[fi] = { ...fields[fi], placeholder: e.target.value };
                              setFormData(prev => ({ ...prev, form_fields: fields }));
                            }}
                            placeholder="Placeholder text..."
                            className="text-xs h-7"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={uploading} className="bg-blue-600 hover:bg-blue-700">
              {editingPage ? 'Save Changes' : 'Create Landing Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Settings Dialog */}
      <Dialog open={isGlobalSettingsOpen} onOpenChange={setIsGlobalSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={18} /> Global Landing Page Settings
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Logo */}
            <div>
              <Label className="font-semibold">Header Logo</Label>
              <UploadHint type="logo" />
              <div className="mt-2">
                {globalSettings.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img 
                      src={`${API_URL}${globalSettings.logo_url}`}
                      alt="Logo"
                      className="h-12 object-contain bg-slate-100 rounded px-2"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setGlobalSettings(prev => ({ ...prev, logo_url: '' }))}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center h-16 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                    <Upload size={20} className="text-slate-400 mr-2" />
                    <span className="text-sm text-slate-500">Upload Logo (240×80px PNG recommended)</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={(e) => handleUploadImage(e.target.files?.[0], 'logo', 'logo')}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Phone */}
            <div>
              <Label>Phone Number</Label>
              <Input
                value={globalSettings.phone_number}
                onChange={(e) => setGlobalSettings(prev => ({ ...prev, phone_number: e.target.value }))}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            {/* Social Links */}
            <div className="space-y-3">
              <Label className="font-semibold">Social Links (leave empty to hide)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Facebook</Label>
                  <Input
                    value={globalSettings.facebook_url}
                    onChange={(e) => setGlobalSettings(prev => ({ ...prev, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Instagram</Label>
                  <Input
                    value={globalSettings.instagram_url}
                    onChange={(e) => setGlobalSettings(prev => ({ ...prev, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">YouTube</Label>
                  <Input
                    value={globalSettings.youtube_url}
                    onChange={(e) => setGlobalSettings(prev => ({ ...prev, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/..."
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">LinkedIn</Label>
                  <Input
                    value={globalSettings.linkedin_url}
                    onChange={(e) => setGlobalSettings(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/..."
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">WhatsApp</Label>
                  <Input
                    value={globalSettings.whatsapp_url}
                    onChange={(e) => setGlobalSettings(prev => ({ ...prev, whatsapp_url: e.target.value }))}
                    placeholder="https://wa.me/..."
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGlobalSettingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveGlobalSettings} className="bg-blue-600 hover:bg-blue-700">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
